import type { ApolloCache } from "@apollo/client/cache"
import cloneDeep from "lodash-es/cloneDeep"

import { groupBy } from "shared/lib/arrayUtils"
import { comparePosition } from "shared/lib/sorting/comparePosition"
import { ProjectChild, ProjectParentTypename } from "shared/types/Project"

import { getProjectChildren } from "./getProjectChildren"
import { sortProjectChildrenAndUpdateIndex } from "./sortProjectChildren"
import { updateProjectChildren } from "./updateProjectChildren"

export function insertProjectChildrenIntoParent({
  children,
  parentID,
  parentType,
  cache
}: {
  children: ProjectChild[]
  parentID: string
  parentType: ProjectParentTypename
  cache: ApolloCache<object>
}) {
  let parentChildren = sortProjectChildrenAndUpdateIndex(
    getProjectChildren(parentID, parentType, cache) || []
  )

  const sortedChildren = cloneDeep(children).sort(comparePosition)

  sortedChildren.forEach(child => {
    parentChildren.splice(child.position, 0, child)
  })

  parentChildren = parentChildren.map((child, index) => ({
    ...child,
    position: index
  }))

  updateProjectChildren({
    parentID,
    parentType: parentType as ProjectParentTypename,
    children: parentChildren,
    cache
  })
}

export function insertProjectChildren({
  children,
  cache
}: {
  children: (ProjectChild & {
    parent?: { id: string; type: ProjectParentTypename }
  })[]
  cache: ApolloCache<object>
}) {
  const childrenWithParent = children.filter(
    child => !!child.parent
  ) as (ProjectChild & {
    parent: { id: string; type: ProjectParentTypename }
  })[]
  const childrenByParent = groupBy(childrenWithParent, child => child.parent.id)

  Object.entries(childrenByParent).forEach(([parentID, childrenByParentID]) => {
    const parentType = childrenByParentID[0].parent.type
    if (!parentType) {
      throw Error("No API Type For Parent Found")
    }
    insertProjectChildrenIntoParent({
      children: childrenByParentID,
      parentID,
      parentType,
      cache
    })
  })
}
